import type {
  GuestCommunicationChannel,
  GuestCommunicationMessageKey,
  PropertyCommunicationCatalogResponse,
  PropertyCommunicationChannelResponse,
  UpdatePropertyCommunicationChannelRequest,
  UpdatePropertyCommunicationCatalogRequest,
} from '@/shared/api';

/** Leer und nur Leerzeichen heißen beim Server „Standard“ — gesendet wird dann nichts. */
export const own = (value: string | null | undefined) => (value?.trim() ? value : null);

export function cloneCatalog(catalog: PropertyCommunicationCatalogResponse) {
  return {
    ...catalog,
    messages: catalog.messages.map((message) => ({
      ...message,
      channels: message.channels.map((channel) => ({
        ...channel,
        defaults: { ...channel.defaults },
        placeholders: channel.placeholders?.map((placeholder) => ({
          ...placeholder,
          allowedFields: [...placeholder.allowedFields],
        })),
      })),
    })),
  };
}

export function hasOverride(channel: PropertyCommunicationChannelResponse) {
  return Object.entries(channel).some(
    ([key, value]) => key !== 'channel' && typeof value === 'string' && value.trim().length > 0,
  );
}

export function toUpdateRequest(
  catalog: PropertyCommunicationCatalogResponse,
): UpdatePropertyCommunicationCatalogRequest {
  return {
    expectedConcurrency: catalog.concurrency ?? null,
    messages: catalog.messages.map(({ key, isEnabled, channels }) => ({
      key,
      isEnabled,
      channels: channels.map(toChannelRequest),
    })),
  };
}

export function toChannelRequest(
  channel: PropertyCommunicationChannelResponse,
): UpdatePropertyCommunicationChannelRequest {
  const { subjectDe, subjectEn, headingDe, headingEn, bodyDe, bodyEn, ctaLabelDe, ctaLabelEn } =
    channel;
  // Ein geleertes Feld bleibt im Entwurf leer, der Server lehnt "" aber ab: gesendet wird null.
  return {
    channel: channel.channel,
    subjectDe: own(subjectDe),
    subjectEn: own(subjectEn),
    headingDe: own(headingDe),
    headingEn: own(headingEn),
    bodyDe: own(bodyDe),
    bodyEn: own(bodyEn),
    ctaLabelDe: own(ctaLabelDe),
    ctaLabelEn: own(ctaLabelEn),
  };
}

export function resetChannel(channel: PropertyCommunicationChannelResponse) {
  return {
    ...channel,
    subjectDe: null,
    subjectEn: null,
    headingDe: null,
    headingEn: null,
    bodyDe: null,
    bodyEn: null,
    ctaLabelDe: null,
    ctaLabelEn: null,
  };
}

export function toggleMessage(
  catalog: PropertyCommunicationCatalogResponse,
  key: GuestCommunicationMessageKey,
  enabled: boolean,
) {
  const linked = key === 'Reminder1d' || key === 'ArrivalDayNudge';
  return {
    ...catalog,
    messages: catalog.messages.map((message) => ({
      ...message,
      isEnabled:
        message.key === key ||
        (linked && (message.key === 'Reminder1d' || message.key === 'ArrivalDayNudge'))
          ? enabled
          : message.isEnabled,
    })),
  };
}

/**
 * Übernimmt gespeicherte Schalter und die neue Revision in den Entwurf. Seine Texte bleiben, wie sie
 * sind — ein Schalter speichert sofort, ungespeicherte Texte warten auf „Speichern".
 */
export function withSwitches(
  draft: PropertyCommunicationCatalogResponse,
  saved: PropertyCommunicationCatalogResponse,
) {
  return {
    ...draft,
    concurrency: saved.concurrency,
    messages: draft.messages.map((message) => ({
      ...message,
      isEnabled:
        saved.messages.find((item) => item.key === message.key)?.isEnabled ?? message.isEnabled,
    })),
  };
}

/** Ändert genau einen Kanal eines Anlasses und lässt alles andere unverändert. */
export function mapChannel(
  catalog: PropertyCommunicationCatalogResponse | null,
  key: GuestCommunicationMessageKey,
  channel: GuestCommunicationChannel,
  update: (value: PropertyCommunicationChannelResponse) => PropertyCommunicationChannelResponse,
) {
  if (!catalog) return catalog;
  return {
    ...catalog,
    messages: catalog.messages.map((message) =>
      message.key === key
        ? {
            ...message,
            channels: message.channels.map((item) =>
              item.channel === channel ? update(item) : item,
            ),
          }
        : message,
    ),
  };
}
